import { cloneElement } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ANALYTICS_BODY_FONT_FAMILY, ANALYTICS_TITLE_FONT_FAMILY } from '../chartTypography';
import {
  ANALYTICS_ICON_HOVER,
  ANALYTICS_TEXT_MUTED,
  ANALYTICS_TEXT_PRIMARY,
  ANALYTICS_TEXT_SUBTLE,
  BROADSIGHT_PRIMARY_BLUE,
} from '../analyticsColors';

const useStyles = makeStyles(() => ({
  headerSection: {
    flex: '0 0 auto',
    height: 'auto',
    minHeight: '100px',
    padding: '16px 16px 8px',
    marginBottom: '8px',
    boxSizing: 'border-box',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    gap: '0',
    position: 'relative',
    zIndex: 3,
  },
  headerSectionAccent: {
    borderLeft: `2px solid ${BROADSIGHT_PRIMARY_BLUE}`,
    paddingLeft: '14px',
  },
  headerSectionWithBadges: {
    marginBottom: '18px',
  },
  iconRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '32px',
    minHeight: '32px',
    maxHeight: '32px',
    marginBottom: '7px',
    width: '100%',
    gap: '8px',
    overflow: 'visible',
    position: 'relative',
    zIndex: 1,
  },
  iconRowSpacer: {
    flex: '1 1 auto',
    minWidth: 0,
    height: '32px',
    pointerEvents: 'none',
  },
  textColumn: {
    minWidth: 0,
    width: '100%',
    display: 'grid',
    rowGap: '2px',
    flex: '1 1 0',
    maxWidth: '100%',
    paddingRight: 0,
    minHeight: '58px',
    overflow: 'visible',
    justifyItems: 'start',
  },
  sectionLabel: {
    fontSize: '10.5px',
    lineHeight: 1.2,
    fontWeight: 600,
    fontFamily: ANALYTICS_TITLE_FONT_FAMILY,
    color: BROADSIGHT_PRIMARY_BLUE,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  title: {
    fontSize: '15px',
    lineHeight: 1.2,
    fontWeight: 600,
    fontFamily: ANALYTICS_TITLE_FONT_FAMILY,
    color: `var(--text-primary, ${ANALYTICS_TEXT_PRIMARY})`,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    marginBottom: 0,
    textAlign: 'left',
    width: '100%',
  },
  subTitle: {
    fontSize: '12.5px',
    lineHeight: 1.2,
    fontWeight: 400,
    fontFamily: ANALYTICS_BODY_FONT_FAMILY,
    color: ANALYTICS_TEXT_MUTED,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    marginBottom: 0,
    textAlign: 'left',
    width: '100%',
  },
  helperText: {
    fontSize: '12px',
    lineHeight: 1.25,
    fontWeight: 400,
    fontFamily: ANALYTICS_BODY_FONT_FAMILY,
    color: ANALYTICS_TEXT_SUBTLE,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: 0,
    textAlign: 'left',
    width: '100%',
  },
  badgesRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '6px',
    minHeight: '20px',
    maxHeight: '24px',
    overflow: 'hidden',
    marginTop: '4px',
    marginBottom: '8px',
    flex: '0 0 auto',
    minWidth: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '20px',
    borderRadius: '11px',
    padding: '4px 8px',
    fontSize: '11.5px',
    lineHeight: 1.1,
    fontFamily: ANALYTICS_BODY_FONT_FAMILY,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: ANALYTICS_TEXT_PRIMARY,
    border: '1px solid transparent',
    boxSizing: 'border-box',
  },
  badgeIssue: {
    backgroundColor: '#ff9797',
    borderColor: '#ff7f7f',
  },
  badgeSupport: {
    backgroundColor: '#e1efd9',
    borderColor: '#cde3be',
  },
  badgeReactive: {
    backgroundColor: '#FFBDAD',
    borderColor: '#f2a692',
  },
  badgeProactive: {
    backgroundColor: '#e1efd9',
    borderColor: '#cde3be',
  },
  badgeStatus: {
    backgroundColor: '#E2F6CA',
    borderColor: '#c8e7a8',
  },
  badgeType: {
    backgroundColor: '#B3F5FF',
    borderColor: '#95e6f3',
  },
  badgeComplexity: {
    backgroundColor: '#F6D15B',
    borderColor: '#e3be44',
  },
  badgeDefault: {
    backgroundColor: '#fff',
    borderColor: 'rgba(0, 0, 0, 0.16)',
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '6px',
    width: 'auto',
    height: '32px',
    minHeight: '32px',
    maxHeight: '32px',
    flex: '0 0 auto',
    overflow: 'visible',
    position: 'relative',
    zIndex: 2,
    minWidth: 'max-content',
    pointerEvents: 'auto',
  },
  actionsRowTeam: {
    gap: '10px',
  },
  actionButton: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    minHeight: '32px',
    borderRadius: '7px',
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
    lineHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: `var(--text-secondary, ${ANALYTICS_TEXT_MUTED})`,
    '& .MuiSvgIcon-root': {
      fontSize: '16px',
      width: '16px',
      height: '16px',
      display: 'block',
    },
    '&:hover': {
      backgroundColor: ANALYTICS_ICON_HOVER,
    },
    position: 'relative',
    zIndex: 3,
    pointerEvents: 'auto',
  },
  actionButtonTeam: {
    width: '34px',
    height: '34px',
    minWidth: '34px',
    minHeight: '34px',
  },
  actionIcon: {
    fontSize: '16px',
    width: '16px',
    height: '16px',
    display: 'block',
  },
  actionIconTeam: {
    fontSize: '18px',
    width: '18px',
    height: '18px',
  },
  actionPlaceholder: {
    width: '34px',
    height: '34px',
    minWidth: '34px',
    minHeight: '34px',
    visibility: 'hidden',
    pointerEvents: 'none',
  },
}));

const getBadgeClassName = (classes, badge) => {
  const normalizedBadgeVariant = typeof badge?.variant === 'string' ? badge.variant.trim().toLowerCase() : '';
  const normalizedBadgeLabel = typeof badge?.label === 'string' ? badge.label.trim().toLowerCase() : '';

  if (normalizedBadgeVariant === 'issue' || normalizedBadgeLabel === 'issue') {
    return classes.badgeIssue;
  }
  if (normalizedBadgeVariant === 'support' || normalizedBadgeLabel === 'support') {
    return classes.badgeSupport;
  }
  if (normalizedBadgeVariant === 'reactive' || normalizedBadgeLabel === 'reactive') {
    return classes.badgeReactive;
  }
  if (normalizedBadgeVariant === 'proactive' || normalizedBadgeLabel === 'proactive') {
    return classes.badgeProactive;
  }
  if (normalizedBadgeVariant === 'status' || normalizedBadgeLabel === 'status') {
    return classes.badgeStatus;
  }
  if (normalizedBadgeVariant === 'type' || normalizedBadgeLabel === 'type') {
    return classes.badgeType;
  }
  if (normalizedBadgeVariant === 'complexity' || normalizedBadgeLabel === 'complexity') {
    return classes.badgeComplexity;
  }
  return classes.badgeDefault;
};

const collapseParenthesizedSegments = (value) => {
  let result = '';
  let index = 0;

  while (index < value.length) {
    if (value[index] !== '(') {
      result += value[index];
      index += 1;
      continue;
    }

    const closingIndex = value.indexOf(')', index + 1);
    if (closingIndex === -1) {
      result += value[index];
      index += 1;
      continue;
    }

    // Mirror `\s*\([^)]*\)\s*` by trimming surrounding whitespace and replacing with one space.
    result = result.replace(/\s*$/, '');
    index = closingIndex + 1;

    while (index < value.length && /\s/.test(value[index])) {
      index += 1;
    }

    result += ' ';
  }

  return result;
};

function AnalyticsCardHeader({
  title,
  subTitle,
  helperText,
  badges = [],
  sectionLabel,
  showSectionAccent = false,
  actions = [],
  actionLayoutVariant = 'default',
}) {
  const classes = useStyles();
  const isTeamLayout = actionLayoutVariant === 'team';
  const actionsRowClassName = isTeamLayout ? `${classes.actionsRow} ${classes.actionsRowTeam}` : classes.actionsRow;
  const actionButtonClassName = isTeamLayout
    ? `${classes.actionButton} ${classes.actionButtonTeam}`
    : classes.actionButton;
  const actionIconClassName = isTeamLayout ? `${classes.actionIcon} ${classes.actionIconTeam}` : classes.actionIcon;
  const normalizedSubTitle =
    typeof subTitle === 'string'
      ? collapseParenthesizedSegments(subTitle)
          .replaceAll(/\s+\?/g, '?')
          .replace(/\.\s*$/, '')
          .replaceAll(/\s{2,}/g, ' ')
          .trim()
      : subTitle;
  const filteredBadges = Array.isArray(badges)
    ? badges
        .map((badge) => {
          if (typeof badge === 'string') {
            const label = badge.trim();
            return {
              label,
              variant: 'neutral',
              tooltip: `${label} badge`,
              missingTooltip: true,
            };
          }

          const label = typeof badge?.label === 'string' ? badge.label.trim() : '';
          const tooltip = typeof badge?.tooltip === 'string' ? badge.tooltip.trim() : '';
          return {
            label,
            variant: typeof badge?.variant === 'string' ? badge.variant.trim() : 'neutral',
            tooltip: tooltip || `${label} badge`,
            missingTooltip: !tooltip,
          };
        })
        .filter((badge) => badge.label)
        .map((badge) => {
          if (process.env.NODE_ENV !== 'production' && badge.missingTooltip) {
            console.warn(`Analytics badge missing tooltip: "${badge.label}"`);
          }
          return {
            ...badge,
            tooltip: badge.tooltip?.trim() ? badge.tooltip.trim() : `${badge.label} badge`,
          };
        })
    : [];
  const normalizedSectionLabel = typeof sectionLabel === 'string' ? sectionLabel.trim() : '';
  const headerClassName = [
    classes.headerSection,
    showSectionAccent ? classes.headerSectionAccent : '',
    filteredBadges.length > 0 ? classes.headerSectionWithBadges : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={headerClassName}>
      <div className={classes.iconRow}>
        <div className={classes.iconRowSpacer} />
        <div className={actionsRowClassName} data-analytics-card-actions>
          {actions.map((action) => {
            if (action.placeholder) {
              return <span key={action.key} className={classes.actionPlaceholder} aria-hidden="true" />;
            }

            return (
              <Tooltip key={action.key} title={action.tooltip} arrow>
                <span>
                  <IconButton
                    className={actionButtonClassName}
                    onClick={action.onClick}
                    aria-label={action.ariaLabel ?? action.tooltip}
                    disabled={Boolean(action.disabled)}
                  >
                    {action.icon ? cloneElement(action.icon, { className: actionIconClassName }) : null}
                  </IconButton>
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
      <div className={classes.textColumn}>
        {normalizedSectionLabel && <Typography className={classes.sectionLabel}>{normalizedSectionLabel}</Typography>}
        <Typography className={classes.title}>{title}</Typography>
        {normalizedSubTitle && <Typography className={classes.subTitle}>{normalizedSubTitle}</Typography>}
        {helperText && <Typography className={classes.helperText}>{helperText}</Typography>}
        {filteredBadges.length > 0 && (
          <div className={classes.badgesRow}>
            {filteredBadges.map((badge, index) => {
              const badgeElement = (
                <span
                  key={`${title}-${badge.label}-${badge.variant}-${index}`}
                  className={`${classes.badge} ${getBadgeClassName(classes, badge)}`}
                >
                  {badge.label}
                </span>
              );

              return (
                <Tooltip key={`${title}-${badge.label}-${badge.variant}-${index}-tooltip`} title={badge.tooltip} arrow>
                  {badgeElement}
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

AnalyticsCardHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  helperText: PropTypes.string,
  badges: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        variant: PropTypes.string,
        tooltip: PropTypes.string,
      }),
    ])
  ),
  sectionLabel: PropTypes.string,
  showSectionAccent: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      ariaLabel: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.element,
      disabled: PropTypes.bool,
      placeholder: PropTypes.bool,
    })
  ),
  actionLayoutVariant: PropTypes.oneOf(['default', 'team']),
};

export default AnalyticsCardHeader;
